import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Ban, CalendarDays, Clock, Lock, MapPin, Users } from "lucide-react";
import { GROUPS, GROUP_CARDS, SEDES, scrollToId, useLang } from "@/i18n";

const CARD_ORDER = ["kkids", "k1_0", "k1", "k2", "k3"];

export default function DanceGroups() {
  const { t } = useLang();
  const g = t.groups;
  const [k3Open, setK3Open] = useState(false);
  const [k3Form, setK3Form] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);

  const byCode = Object.fromEntries(GROUPS.map((gr) => [gr.code, gr]));

  const daysText = (gr) => gr.days.map((d) => g.daysLabel[d]).join(` ${g.join} `);

  const submitK3 = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/k3-interest`, k3Form);
      toast.success(g.k3.ok, { style: { background: "#14141A", border: "1px solid #FF007A" } });
      setK3Form({ name: "", email: "" });
      setK3Open(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || g.k3.err);
    } finally {
      setSending(false);
    }
  };

  const inputK3 =
    "w-full rounded-lg border border-amber-400/30 bg-black/30 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-amber-400";

  return (
    <section id="grupos" className="px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto max-w-7xl">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent"
        >
          {g.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl"
        >
          {g.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base"
        >
          {g.subtitle}
        </motion.p>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CARD_ORDER.map((key, i) => {
            const card = GROUP_CARDS[key];
            const gr = byCode[card.key];
            const isK3 = card.key === "K3";
            const isFull = gr?.status === "full";

            if (isK3) {
              return (
                <motion.article
                  key={key}
                  data-testid="group-card-k3-reopening"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex flex-col overflow-hidden rounded-2xl border-2 border-dashed border-amber-400/60 bg-gradient-to-b from-[#1A1408] to-card"
                >
                  <div className="relative h-52 overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_65%,rgba(255,0,122,0.4)_0%,#26000F_75%)]" />
                    <p className="absolute inset-0 flex items-center justify-center font-display text-[6rem] font-black uppercase leading-none text-accent drop-shadow-[0_0_40px_rgba(255,0,122,0.65)]">
                      K3
                    </p>
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(0deg,rgba(20,16,8,0.9)_0%,transparent_50%)]" />
                    <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-black">
                      <Lock size={11} /> {g.k3.badge}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-amber-300">{g.k3.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">{g.k3.note}</p>
                    <p className="mt-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300">{g.k3.reopening}</p>

                    {!k3Open ? (
                      <button
                        type="button"
                        data-testid="k3-reopening-info-button"
                        onClick={() => setK3Open(true)}
                        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-amber-400/70 px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-amber-300 transition-all duration-300 hover:bg-amber-400 hover:text-black"
                      >
                        {g.k3.cta}
                      </button>
                    ) : (
                      <form data-testid="k3-interest-form" onSubmit={submitK3} className="mt-5 space-y-3">
                        <input required data-testid="k3-interest-name" value={k3Form.name} onChange={(e) => setK3Form((f) => ({ ...f, name: e.target.value }))} placeholder={g.k3.phName} className={inputK3} />
                        <input required type="email" data-testid="k3-interest-email" value={k3Form.email} onChange={(e) => setK3Form((f) => ({ ...f, email: e.target.value }))} placeholder={g.k3.phEmail} className={inputK3} />
                        <div className="flex gap-2">
                          <button type="submit" data-testid="k3-interest-submit" disabled={sending} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-black transition-all duration-300 hover:brightness-110 disabled:opacity-60">
                            {sending ? g.k3.sending : g.k3.send}
                          </button>
                          <button type="button" data-testid="k3-interest-cancel" onClick={() => setK3Open(false)} className="rounded-full border border-white/20 px-4 py-3 font-mono text-xs font-bold uppercase text-zinc-400 transition-colors hover:text-white">
                            ✕
                          </button>
                        </div>
                      </form>
                    )}
                    <p className="mt-4 flex items-center gap-1.5 text-[11px] text-zinc-500">
                      <Ban size={12} /> {g.k3.badge} · {g.k3.reopening}
                    </p>
                  </div>
                </motion.article>
              );
            }

            return (
              <motion.article
                key={key}
                data-testid={isFull ? "group-card-k2-closed" : `group-card-${card.key.replace(".", "-").toLowerCase()}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-card transition-all duration-300 hover:border-accent/60 hover:shadow-[0_0_40px_rgba(255,0,122,0.15)]"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={card.photo} alt={`${card.key} · grupo de baile K-HOUSE`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(8,8,10,0.85)_0%,transparent_55%)]" />
                  {isFull ? (
                    <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                      <Ban size={11} /> {g.fullBadge}
                    </span>
                  ) : (
                    <span className="absolute left-4 top-4 rounded-full bg-emerald-500/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                      {g.openBadge}
                    </span>
                  )}
                  <span className="absolute bottom-3 left-4 rounded-md bg-accent px-2.5 py-1 font-display text-lg font-black uppercase text-white">
                    {card.key}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <p className="text-sm leading-relaxed text-zinc-300">{g.cards[key].d}</p>

                  <div className="mt-4 space-y-2 font-mono text-xs text-zinc-400">
                    {gr.level && (
                      <p className="flex items-center gap-2">
                        <Users size={13} className="text-accent" /> {gr.level}
                      </p>
                    )}
                    <p className="flex items-center gap-2" data-testid={`group-schedule-${card.key.replace(".", "-").toLowerCase()}`}>
                      <CalendarDays size={13} className="text-accent" /> {daysText(gr)} · {gr.time}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin size={13} className="text-accent" /> {SEDES[gr.sede].name}
                    </p>
                  </div>

                  {!isFull && (
                    <button
                      type="button"
                      data-testid={`group-cta-${card.key.replace(".", "-").toLowerCase()}`}
                      onClick={() => scrollToId("contacto")}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-accent/70 px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-accent transition-all duration-300 hover:bg-accent hover:text-white"
                    >
                      {g.cta} <ArrowUpRight size={14} />
                    </button>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}